import { forwardRef } from 'react';
import { Shield, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PremiumLogoProps {
  showTagline?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const PremiumLogo = forwardRef<HTMLAnchorElement, PremiumLogoProps>(
  ({ showTagline = true, size = 'md' }, ref) => {
    const sizes = {
      sm: {
        container: 'w-10 h-10',
        icon: 'w-5 h-5',
        badge: 'w-4 h-4',
        badgeIcon: 'w-2.5 h-2.5',
        title: 'text-lg',
        tagline: 'text-[9px]',
      },
      md: {
        container: 'w-12 h-12',
        icon: 'w-7 h-7',
        badge: 'w-5 h-5',
        badgeIcon: 'w-3 h-3',
        title: 'text-xl sm:text-2xl',
        tagline: 'text-[10px] sm:text-[11px]',
      },
      lg: {
        container: 'w-16 h-16',
        icon: 'w-9 h-9',
        badge: 'w-6 h-6',
        badgeIcon: 'w-4 h-4',
        title: 'text-2xl sm:text-3xl',
        tagline: 'text-xs',
      },
    };

    const s = sizes[size];

    return (
      <Link ref={ref} to="/" className="flex items-center gap-4 group">
        <div className="relative">
          {/* Outer animated glow */}
          <div className="absolute -inset-3 rounded-2xl opacity-60 blur-xl animate-color-shift" 
            style={{ 
              background: 'linear-gradient(135deg, hsl(270 95% 60%), hsl(340 85% 60%), hsl(20 100% 60%))',
            }} 
          />
          
          {/* Secondary glow ring */}
          <div className="absolute -inset-1.5 rounded-2xl gradient-bg opacity-80 blur-sm group-hover:opacity-100 transition-opacity animate-pulse-slow" />
          
          {/* Main logo container */}
          <div className={`relative ${s.container} rounded-2xl overflow-hidden shadow-glow-intense group-hover:scale-110 transition-transform duration-300`}>
            {/* Animated gradient background */}
            <div className="absolute inset-0 gradient-bg animate-gradient-shift" style={{ backgroundSize: '300% 300%' }} />
            
            {/* Glass overlay */}
            <div className="absolute inset-0 bg-white/10" />
            
            {/* Icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <Shield className={`${s.icon} text-primary-foreground drop-shadow-lg`} strokeWidth={2.5} />
                {/* Inner keyhole */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground/60 mt-1.5 animate-pulse" />
                </div>
              </div>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
          
          {/* Premium badge */}
          <div className={`absolute -bottom-1 -right-1 ${s.badge} rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 flex items-center justify-center shadow-lg border-2 border-background animate-bounce-subtle`}>
            <Crown className={`${s.badgeIcon} text-white drop-shadow-sm`} />
          </div>
          
          {/* Status indicator */}
          <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full gradient-bg shadow-glow animate-glow-pulse" />
        </div>
        
        <div className="flex flex-col">
          <h1 className={`${s.title} font-extrabold tracking-tight`}>
            <span className="bg-gradient-to-r from-primary via-accent to-warning bg-clip-text text-transparent bg-[length:300%_auto] animate-gradient text-glow">
              TempVault
            </span>
            <span className="ml-1 text-xs font-bold text-amber-500 align-super animate-bounce-subtle">PRO</span>
          </h1>
          {showTagline && (
            <p className={`${s.tagline} text-muted-foreground hidden sm:block font-semibold tracking-widest uppercase animate-fade-in`}>
              Private Disposable Email
            </p>
          )}
        </div>
      </Link>
    );
  }
);

PremiumLogo.displayName = 'PremiumLogo';
